// @spec AI-STR-030
'use client';

import { useUser } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { BrainCircuit, ShieldAlert } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import {
  LlmSettingsForm,
  useLlmConfig,
  useTestLlmConnection,
  useUpdateLlmConfig,
} from '@/features/admin';
import type { LlmConfigUpdate } from '@/features/admin';

export const Route = createFileRoute('/_authenticated/admin/llm-settings')({
  component: LlmSettingsPage,
});

/**
 * Check if user has Super Admin role from Clerk metadata.
 */
function isSuperAdmin(user: ReturnType<typeof useUser>['user']): boolean {
  if (!user) return false;

  const publicMetadata = user.publicMetadata as Record<string, unknown> | undefined;
  return publicMetadata?.role === 'super_admin';
}

function LlmSettingsPage() {
  const { user, isLoaded: userLoaded } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Check Super Admin access
  const hasAccess = isSuperAdmin(user);

  // Fetch LLM config
  const { data: config, isLoading, error: fetchError } = useLlmConfig();

  // Update mutation
  const { mutateAsync: updateConfig, isPending: isUpdating } = useUpdateLlmConfig();

  // Test connection mutation
  const { mutateAsync: testConnection, isPending: isTesting } = useTestLlmConnection();

  const handleSubmit = useCallback(
    async (data: LlmConfigUpdate) => {
      setSuccessMessage(null);
      setErrorMessage(null);

      try {
        await updateConfig(data);
        setSuccessMessage('LLM configuration updated successfully.');
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update configuration';
        setErrorMessage(message);
      }
    },
    [updateConfig]
  );

  const handleTest = useCallback(async () => {
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      const result = await testConnection();
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to test connection';
      setErrorMessage(message);
      return {
        success: false,
        message,
        testedAt: new Date().toISOString(),
        errorCode: 'test_failed',
      };
    }
  }, [testConnection]);

  // Show loading while checking user
  if (!userLoaded) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-3xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-muted rounded" />
            <div className="h-4 w-64 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  // Show 403 if not Super Admin
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-lg text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-destructive/10 p-4">
              <ShieldAlert className="h-12 w-12 text-destructive" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">{t('accessDenied.title')}</h1>
          <p className="text-muted-foreground mb-6">
            {t('accessDenied.superAdminRequired')}
          </p>
          <button
            type="button"
            className="text-primary-600 hover:underline"
            onClick={() => {
              void navigate({ to: '/dashboard' });
            }}
          >
            {t('accessDenied.returnToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  // Show error state
  if (fetchError) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-destructive">Failed to load LLM configuration. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show loading skeleton
  if (isLoading || !config) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-3xl space-y-6">
          {/* Header skeleton */}
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-64" />
            </div>
          </div>

          {/* Card skeletons */}
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-xl border p-6 space-y-4">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-48" />
              <div className="space-y-2">
                <Skeleton className="h-9 w-40" />
                <Skeleton className="h-9 w-40" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary-100 p-2 dark:bg-primary-900">
            <BrainCircuit className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">LLM Settings</h1>
            <p className="text-sm text-muted-foreground">
              Configure AI Advisor model settings and API credentials
            </p>
          </div>
        </div>

        {/* Success message */}
        {successMessage && (
          <div className="rounded-md border border-green-500/50 bg-green-500/10 p-4">
            <p className="text-sm text-green-700 dark:text-green-400">{successMessage}</p>
          </div>
        )}

        {/* Error message */}
        {errorMessage && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-sm text-destructive">{errorMessage}</p>
          </div>
        )}

        {/* Settings form */}
        <LlmSettingsForm
          initialData={config}
          onSubmit={handleSubmit}
          onTest={handleTest}
          isLoading={isUpdating}
          isTesting={isTesting}
        />
      </div>
    </div>
  );
}
